import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  onSnapshot,
  writeBatch,
  Timestamp,
  increment,
  where
} from 'firebase/firestore';
import { APP_ID } from '../config/constants';
import { checkChange } from '../ledger/effects';
const TRANSFERS_COLLECTION = 'transfers';

/**
 * Get Firestore instance
 */
const getDb = () => {
  return getFirestore();
};

/**
 * Get transfers collection reference for a specific user
 */
const getTransfersRef = (userId) => {
  const db = getDb();
  return collection(db, 'artifacts', APP_ID, 'users', userId, TRANSFERS_COLLECTION);
};

/**
 * Transfer money between sources
 */
export const transferMoney = async (userId, transferData) => {
  const {
    fromSourceId,
    toSourceId,
    amount,
    transferDate,
    note,
    fee = 0
  } = transferData;

  if (fromSourceId === toSourceId) {
    throw new Error('Không thể chuyển tiền cho cùng một tài khoản');
  }

  if (amount <= 0) {
    throw new Error('Số tiền chuyển phải lớn hơn 0');
  }

  const db = getDb();

  try {
    const fromSourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', fromSourceId);
    const toSourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'sources', toSourceId);

    // Chặn thẻ tín dụng: chuyển tiền chỉ áp dụng cho nguồn có số dư thật.
    // Trả nợ thẻ có luồng riêng (Thanh toán thẻ) cập nhật usedCredit đúng cách.
    const [fromSnap, toSnap] = await Promise.all([getDoc(fromSourceRef), getDoc(toSourceRef)]);
    if (!fromSnap.exists() || !toSnap.exists()) {
      throw new Error('Không tìm thấy tài khoản nguồn hoặc đích');
    }
    if (fromSnap.data().type === 'credit' || toSnap.data().type === 'credit') {
      throw new Error('Không thể chuyển tiền vào/ra thẻ tín dụng — dùng tab Thanh toán thẻ để trả dư nợ');
    }

    const totalDeduct = amount + fee;
    // Cùng chính sách với sổ cái: ví tiền mặt được âm, ví ngân hàng chặn khi thiếu tiền
    const policy = checkChange(fromSnap.data(), 'balance', -totalDeduct);
    if (!policy.ok) throw new Error(policy.error);

    // TOÀN BỘ các lệnh ghi trong MỘT batch nguyên tử: bản ghi chuyển tiền,
    // 2 cập nhật số dư, 2 dòng audit, và khoản chi phí (nếu có) —
    // cùng thành công hoặc cùng thất bại, không còn "giao dịch ma"
    const batch = writeBatch(db);
    const now = Timestamp.now();
    const txDate = Timestamp.fromDate(new Date(transferDate));

    const transferRef = doc(getTransfersRef(userId));
    batch.set(transferRef, {
      fromSourceId,
      toSourceId,
      amount,
      fee,
      transferDate: txDate,
      note: note || '',
      createdAt: now,
      status: 'completed'
    });

    batch.update(fromSourceRef, { balance: increment(-totalDeduct), updatedAt: now });
    batch.update(toSourceRef, { balance: increment(amount), updatedAt: now });

    // Audit trail — dùng field timestamp thống nhất với updateBalance/updateCreditUsage
    // (trước đây dùng 'date' khiến lịch sử giao dịch sắp xếp sai thứ tự)
    const transactionsRef = collection(db, 'artifacts', APP_ID, 'users', userId, 'transactions');
    batch.set(doc(transactionsRef), {
      sourceId: fromSourceId,
      amount: -totalDeduct,
      type: 'transfer_out',
      relatedId: transferRef.id,
      description: `Chuyển tiền đến ${toSnap.data().name || toSourceId}${fee > 0 ? ` (phí: ${fee})` : ''}`,
      timestamp: now,
      createdAt: now
    });
    batch.set(doc(transactionsRef), {
      sourceId: toSourceId,
      amount: amount,
      type: 'transfer_in',
      relatedId: transferRef.id,
      description: `Nhận tiền từ ${fromSnap.data().name || fromSourceId}`,
      timestamp: now,
      createdAt: now
    });

    // Phí chuyển tiền = chi phí tài chính: tự sinh bản ghi chi tiêu để báo cáo
    // phản ánh đúng chi phí. KHÔNG trừ ví lần nữa (đã trừ trong totalDeduct) —
    // đây là bản ghi kế toán thuần, đánh dấu autoGenerated.
    if (fee > 0) {
      const expensesRef = collection(db, 'artifacts', APP_ID, 'users', userId, 'expenses');
      const feeDate = new Date(transferDate);
      batch.set(doc(expensesRef), {
        amount: fee,
        category: 'Chi phí tài chính',
        categoryName: 'Chi phí tài chính',
        description: `Phí chuyển tiền (tự động từ giao dịch chuyển ${fromSnap.data().name || ''} → ${toSnap.data().name || ''})`,
        transactionDate: txDate,
        month: feeDate.getMonth() + 1,
        year: feeDate.getFullYear(),
        autoGenerated: true,
        relatedTransferId: transferRef.id,
        createdAt: now
      });
    }

    await batch.commit();

    return {
      success: true,
      data: {
        id: transferRef.id,
        ...transferData,
        status: 'completed'
      }
    };

  } catch (error) {
    console.error('Transfer error:', error);
    throw new Error('Lỗi khi chuyển tiền: ' + error.message);
  }
};

/**
 * Get transfer history
 */
export const getTransferHistory = async (userId, limit = 50) => {
  try {
    const transfersRef = getTransfersRef(userId);
    const q = query(transfersRef, where('status', '==', 'completed'));
    const snapshot = await getDocs(q);

    const transfers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      transferDate: doc.data().transferDate?.toDate?.() || new Date(doc.data().transferDate),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));

    // Sort by date descending
    transfers.sort((a, b) => b.transferDate - a.transferDate);

    return {
      success: true,
      data: transfers.slice(0, limit)
    };
  } catch (error) {
    console.error('Get transfer history error:', error);
    throw new Error('Lỗi khi lấy lịch sử chuyển tiền: ' + error.message);
  }
};

/**
 * Subscribe to transfer history
 */
export const subscribeToTransfers = (userId, callback) => {
  const transfersRef = getTransfersRef(userId);
  const q = query(transfersRef, where('status', '==', 'completed'));

  return onSnapshot(q, (snapshot) => {
    const transfers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      transferDate: doc.data().transferDate?.toDate?.() || new Date(doc.data().transferDate),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
    }));

    // Sort by date descending
    transfers.sort((a, b) => b.transferDate - a.transferDate);

    callback({
      success: true,
      data: transfers
    });
  }, (error) => {
    console.error('Subscribe transfers error:', error);
    callback({
      success: false,
      error: error.message
    });
  });
};

/**
 * Delete transfer (for admin purposes)
 */
export const deleteTransfer = async (userId, transferId) => {
  try {
    const transferRef = doc(getTransfersRef(userId), transferId);
    await updateDoc(transferRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Delete transfer error:', error);
    throw new Error('Lỗi khi xóa giao dịch chuyển tiền: ' + error.message);
  }
};